<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    
    <div class="row">
      <div class="col-12">
        <!-- Card Principal -->
        <div class="card">
          <div class="card-body">
            
            <!-- Header da página com título e ação -->
            <div class="row mb-4">
              <div class="col-sm-8">
                <div class="d-flex align-items-center">
                  <div class="flex-shrink-0 me-3">
                    <div class="avatar-sm rounded-circle bg-soft-primary">
                      <div class="avatar-title rounded-circle bg-primary">
                        <i class="ri-clipboard-line font-size-18 text-white"></i>
                      </div>
                    </div>
                  </div>
                  <div class="flex-grow-1">
                    <h4 class="card-title mb-1">Tipos de Exames</h4>
                    <p class="card-title-desc text-muted mb-0">
                      Gerencie os tipos de exames disponíveis no sistema
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-sm-4">
                <div class="text-sm-right">
                  <button
                    v-if="userCanEdit"
                    class="btn btn-primary btn-rounded waves-effect waves-light"
                    @click="goToNew"
                  >
                    <i class="ri-add-circle-line align-middle me-2"></i>
                    Novo Tipo
                  </button>
                </div>
              </div>
            </div>

            <!-- Cards de Estatísticas -->
            <div class="row mb-4" v-if="statistics">
              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">
                          <i class="ri-file-list-3-line me-2 text-primary"></i>
                          Total de Tipos
                        </p>
                        <h4 class="mb-0 text-primary">{{ statistics.total }}</h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-primary">
                          <span class="avatar-title">
                            <i class="ri-clipboard-line font-size-20"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">
                          <i class="ri-check-line me-2 text-success"></i>
                          Ativos
                        </p>
                        <h4 class="mb-0 text-success">{{ statistics.active }}</h4>
                        <p class="text-muted font-size-12 mb-0">
                          {{ getPercentage(statistics.active, statistics.total) }}% do total
                        </p>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-success">
                          <span class="avatar-title">
                            <i class="ri-check-double-line font-size-20"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">
                          <i class="ri-close-line me-2 text-warning"></i>
                          Inativos
                        </p>
                        <h4 class="mb-0 text-warning">{{ statistics.inactive }}</h4>
                        <p class="text-muted font-size-12 mb-0">
                          {{ getPercentage(statistics.inactive, statistics.total) }}% do total
                        </p>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-warning">
                          <span class="avatar-title">
                            <i class="ri-close-circle-line font-size-20"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-xl-3 col-md-6">
                <div class="card mini-stats-wid border-0 shadow-sm">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">
                          <i class="ri-price-tag-3-line me-2 text-info"></i>
                          Categorias
                        </p>
                        <h4 class="mb-0 text-info">{{ statistics.categories_count }}</h4>
                        <p class="text-muted font-size-12 mb-0">
                          Diferentes tipos
                        </p>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div class="mini-stat-icon avatar-sm rounded-circle bg-info">
                          <span class="avatar-title">
                            <i class="ri-bookmark-line font-size-20"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filtros Avançados -->
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-body py-3">
                <div class="row align-items-center">
                  <div class="col-lg-4 col-md-6 mb-2">
                    <div class="search-box">
                      <div class="position-relative">
                        <input
                          v-model="searchTerm"
                          type="text"
                          class="form-control bg-light border-light rounded"
                          placeholder="Buscar por nome ou código..."
                          @input="debouncedSearch"
                        >
                        <i class="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-lg-2 col-md-3 mb-2">
                    <select 
                      v-model="selectedCategory" 
                      @change="filterByCategory" 
                      class="form-select bg-light border-light"
                    >
                      <option value="">Todas as categorias</option>
                      <option v-for="category in categories" :key="category" :value="category">
                        {{ category }}
                      </option>
                    </select>
                  </div>

                  <div class="col-lg-2 col-md-3 mb-2">
                    <div class="form-check form-switch">
                      <input
                        v-model="showActiveOnly"
                        class="form-check-input"
                        type="checkbox"
                        id="activeOnlySwitch"
                        @change="filterExamTypes"
                      >
                      <label class="form-check-label fw-medium" for="activeOnlySwitch">
                        Apenas ativos
                      </label>
                    </div>
                  </div>

                  <div class="col-lg-4 col-md-12 text-right">
                    <span class="text-muted font-size-13">
                      <i class="ri-filter-line me-1"></i>
                      {{ totalRows }} registro(s) encontrado(s)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabela -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
              </div>
            </div>
            
            <div v-else>
              <!-- Controles da Tabela -->
              <div class="row mt-4 mb-3">
                <div class="col-sm-12 col-md-6">
                  <div class="dataTables_length">
                    <label class="d-inline-flex align-items-center fw-medium">
                      Mostrar&nbsp;
                      <select v-model="perPage" class="form-select form-select-sm mx-2" style="width: auto;">
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>&nbsp;registros por página
                    </label>
                  </div>
                </div>
              </div>

              <!-- Tabela Principal -->
              <div class="table-responsive">
                <table class="table table-hover align-middle table-nowrap mb-0">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="fw-semibold">Código</th>
                      <th scope="col" class="fw-semibold">Nome</th>
                      <th scope="col" class="fw-semibold">Categoria</th>
                      <th scope="col" class="fw-semibold">Status</th>
                      <th scope="col" class="fw-semibold text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="examType in paginatedItems" :key="examType.id" class="border-bottom">
                      <td>
                        <span class="badge badge-soft-primary font-size-12">{{ examType.code }}</span>
                      </td>
                      <td class="fw-medium">{{ examType.name }}</td>
                      <td>
                        <span v-if="examType.category" class="badge badge-soft-info font-size-12">
                          {{ examType.category }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <span 
                          v-if="examType.active" 
                          class="badge badge-soft-success font-size-12"
                        >
                          <i class="ri-check-line me-1"></i>Ativo
                        </span>
                        <span v-else class="badge badge-soft-danger font-size-12">
                          <i class="ri-close-line me-1"></i>Inativo
                        </span>
                      </td>
                      <td class="text-center">
                        <div class="btn-group" role="group">
                          <button 
                            type="button" 
                            class="btn btn-outline-primary btn-sm" 
                            @click="viewExamType(examType)"
                            title="Visualizar"
                          >
                            <i class="ri-eye-line"></i>
                          </button>
                          <button 
                            v-if="userCanEdit"
                            type="button" 
                            class="btn btn-outline-warning btn-sm" 
                            @click="editExamType(examType)"
                            title="Editar"
                          >
                            <i class="ri-pencil-line"></i>
                          </button>
                          <button 
                            v-if="userCanDelete && !examType.active"
                            type="button" 
                            class="btn btn-outline-danger btn-sm" 
                            @click="confirmDelete(examType)"
                            title="Excluir"
                          >
                            <i class="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    <!-- Estado vazio -->
                    <tr v-if="examTypes.length === 0">
                      <td colspan="5" class="text-center py-5">
                        <div class="py-3">
                          <i class="ri-file-list-3-line font-size-48 text-muted mb-3 d-block"></i>
                          <h5 class="text-muted">Nenhum tipo de exame encontrado</h5>
                          <p class="text-muted mb-3">
                            {{ searchTerm ? 'Tente alterar os filtros de busca' : 'Comece criando seu primeiro tipo de exame' }}
                          </p>
                          <button 
                            v-if="userCanEdit && !searchTerm" 
                            class="btn btn-primary"
                            @click="goToNew"
                          >
                            <i class="ri-add-line me-1"></i>Criar Primeiro Tipo
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Paginação -->
              <div class="row mt-4" v-if="totalRows > perPage">
                <div class="col-sm-12 col-md-5">
                  <div class="dataTables_info">
                    Mostrando {{ startIndex }} a {{ endIndex }} de {{ totalRows }} registros
                  </div>
                </div>
                <div class="col-sm-12 col-md-7">
                  <div class="dataTables_paginate">
                    <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      align="right"
                      size="sm"
                      class="pagination-rounded mb-0"
                    ></b-pagination>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import { examTypeService } from '@/api/examTypes.js'
import { authMixin } from '@/mixins/authMixin'
import { debounce } from 'lodash'

export default {
  name: 'ExamTypesList',
  page: {
    title: "Tipos de Exames",
    meta: [{ name: "description", content: "Gerencie os tipos de exames do sistema" }],
  },
  mixins: [authMixin],
  components: {
    Layout,
    PageHeader
  },
  data() {
    return {
      title: "Tipos de Exames",
      breadcrumbItems: [
        {
          text: "Configurações",
          href: "/"
        },
        {
          text: "Tipos de Exames",
          active: true
        }
      ],
      examTypes: [],
      categories: [],
      statistics: null,
      loading: false,
      searchTerm: '',
      selectedCategory: '',
      showActiveOnly: true,
      currentPage: 1,
      perPage: 20,
      totalRows: 0
    }
  },
  computed: {
    userCanEdit() {
      return this.hasRole(['admin', 'technician'])
    },
    userCanDelete() {
      return this.hasRole(['admin'])
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.examTypes.slice(start, end)
    },
    startIndex() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    endIndex() {
      const end = this.currentPage * this.perPage
      return end > this.totalRows ? this.totalRows : end
    }
  },
  created() {
    this.debouncedSearch = debounce(this.searchExamTypes, 500)
  },
  async mounted() {
    await this.loadInitialData()
  },
  methods: {
    getPercentage(value, total) {
      if (!total || total === 0) return 0
      return Math.round((value / total) * 100)
    },

    async loadInitialData() {
      await Promise.all([
        this.loadExamTypes(),
        this.loadCategories(),
        this.loadStatistics()
      ])
    },

    async loadExamTypes() {
      this.loading = true
      try {
        const params = {
          active_only: this.showActiveOnly,
          limit: 1000 // Carregar todos para exibição local
        }

        if (this.searchTerm) {
          params.search = this.searchTerm
        }
        if (this.selectedCategory) {
          params.category = this.selectedCategory
        }

        const response = await examTypeService.listExamTypes(params)
        this.examTypes = response.data || []
        this.totalRows = this.examTypes.length
      } catch (error) {
        console.error('Erro ao carregar tipos de exames:', error)
        this.$showError('Erro ao carregar tipos de exames')
      } finally {
        this.loading = false
      }
    },

    async loadCategories() {
      try {
        const response = await examTypeService.getCategories()
        this.categories = response.data || []
      } catch (error) {
        console.error('Erro ao carregar categorias:', error)
      }
    },

    async loadStatistics() {
      try {
        const response = await examTypeService.getStatistics()
        this.statistics = response.data || null
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error)
      }
    },

    async searchExamTypes() {
      this.currentPage = 1
      await this.loadExamTypes()
    },

    async filterByCategory() {
      this.currentPage = 1
      await this.loadExamTypes()
    },

    async filterExamTypes() {
      this.currentPage = 1
      await this.loadExamTypes()
      await this.loadStatistics()
    },

    goToNew() {
      this.$router.push('/configuration/exam-types/new')
    },

    viewExamType(examType) {
      this.$router.push(`/configuration/exam-types/view/${examType.id}`)
    },

    editExamType(examType) {
      this.$router.push(`/configuration/exam-types/edit/${examType.id}`)
    },

    confirmDelete(examType) {
      if (confirm(`Tem certeza que deseja excluir o tipo de exame "${examType.name}"?`)) {
        this.deleteExamType(examType)
      }
    },

    async deleteExamType(examType) {
      try {
        await examTypeService.deleteExamType(examType.id)
        this.$showSuccess('Tipo de exame removido com sucesso')
        await this.loadInitialData()
      } catch (error) {
        console.error('Erro ao remover tipo de exame:', error)
        this.$showError('Erro ao remover tipo de exame')
      }
    }
  }
}
</script>

<style scoped>
/* Mini Stats Cards */
.mini-stats-wid {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border: 1px solid rgba(0,0,0,0.05);
}

.mini-stats-wid:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

.mini-stat-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Search Box */
.search-box .search-icon {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  color: #74788d;
  pointer-events: none;
}

.search-box input {
  padding-right: 45px;
}

/* Badges */
.badge-soft-primary {
  color: #556ee6;
  background-color: rgba(85, 110, 230, 0.18);
  border: 1px solid rgba(85, 110, 230, 0.1);
}

.badge-soft-success {
  color: #34c38f;
  background-color: rgba(52, 195, 143, 0.18);
  border: 1px solid rgba(52, 195, 143, 0.1);
}

.badge-soft-danger {
  color: #f46a6a;
  background-color: rgba(244, 106, 106, 0.18);
  border: 1px solid rgba(244, 106, 106, 0.1);
}

.badge-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
  border: 1px solid rgba(80, 165, 241, 0.1);
}

.badge-soft-warning {
  color: #f1b44c;
  background-color: rgba(241, 180, 76, 0.18);
  border: 1px solid rgba(241, 180, 76, 0.1);
}

/* Table Enhancements */
.table th {
  border-top: none;
  border-bottom: 2px solid #eef0f7;
  font-weight: 600;
  color: #495057;
  background-color: #f8f9fa;
}

.table td {
  border-color: #eef0f7;
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: rgba(85, 110, 230, 0.04);
}

/* Pagination */
.pagination-rounded .page-link {
  border-radius: 50px !important;
  margin: 0 2px;
  border: none;
  padding: 8px 12px;
}

.pagination-rounded .page-item.active .page-link {
  background-color: #556ee6;
  border-color: #556ee6;
}

/* Buttons */
.btn-rounded {
  border-radius: 30px;
}

.btn:focus, .btn.focus {
  box-shadow: none;
}

.btn-group .btn {
  margin-right: 2px;
}

.btn-group .btn:last-child {
  margin-right: 0;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.25rem;
}

/* Form Controls */
.form-control:focus, .form-select:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 0.2rem rgba(85, 110, 230, 0.25);
}

.bg-light {
  background-color: #f8f9fa !important;
}

.border-light {
  border-color: #e9ecef !important;
}

/* Empty State */
.table tbody tr td {
  padding: 1rem 0.75rem;
}

/* Loading State */
.spinner-border {
  color: #556ee6;
}

/* Cards */
.card {
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.05);
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.08) !important;
}

/* Form Check */
.form-check-input:checked {
  background-color: #556ee6;
  border-color: #556ee6;
}

.form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(85, 110, 230, 0.25);
}

/* Responsive */
@media (max-width: 768px) {
  .mini-stats-wid {
    margin-bottom: 1rem;
  }
  
  .text-sm-right {
    text-align: left !important;
    margin-top: 1rem;
  }
  
  .btn-rounded {
    width: 100%;
  }
  
  .dataTables_length,
  .dataTables_info,
  .dataTables_paginate {
    text-align: center !important;
    margin-bottom: 1rem;
  }
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.card, .mini-stats-wid {
  animation: fadeIn 0.3s ease-out;
}

/* Custom Spacing */
.py-3 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
.py-5 { padding-top: 3rem !important; padding-bottom: 3rem !important; }
.mb-4 { margin-bottom: 1.5rem !important; }
.font-size-12 { font-size: 12px !important; }
.font-size-13 { font-size: 13px !important; }
.font-size-18 { font-size: 18px !important; }
.font-size-20 { font-size: 20px !important; }
.font-size-48 { font-size: 48px !important; }
.fw-semibold { font-weight: 600 !important; }
</style>
